import React from "react";
import { ProductCheckInColumnHeader } from "src/enums";
import { DateQtyInputField, OrderProductCheckInInputField, ReceivingDiscrepancy, TableColumn } from "src/types";
import { DynamicDateQtyInputList } from "src/components";
import { TextInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";

interface OrderProductCheckInInputTableProps {
  orderProductCheckIns: OrderProductCheckInInputField[];
  setOrderProductCheckIns: React.Dispatch<React.SetStateAction<OrderProductCheckInInputField[]>>;
  existingPOReceivingDiscrepancy?: ReceivingDiscrepancy;
}

const OrderProductCheckInInputTable: React.FC<OrderProductCheckInInputTableProps> = ({
  orderProductCheckIns,
  setOrderProductCheckIns,
  existingPOReceivingDiscrepancy
}) => {
  const handleProductCheckInsInputChange = <T,>(
    id: string,
    fieldName: keyof OrderProductCheckInInputField,
    value: T
  ) => {
    setOrderProductCheckIns(
      orderProductCheckIns.map((productCheckIn) =>
        productCheckIn.fieldId === id ? { ...productCheckIn, [fieldName]: value } : productCheckIn
      )
    );
  };

  const handleSplitQtyChange = (id: string, fieldName: "receivedUkQty" | "receivedEuQty", value: string) => {
    setOrderProductCheckIns(
      orderProductCheckIns.map((productCheckIn) => {
        if (productCheckIn.fieldId !== id) {
          return productCheckIn;
        }
        const updated = { ...productCheckIn, [fieldName]: value };
        const ukQty = Number(updated.receivedUkQty) || 0;
        const euQty = Number(updated.receivedEuQty) || 0;
        const total = ukQty + euQty;
        return { ...updated, receivedQty: total ? total.toString() : "" };
      })
    );
  };

  const showWarehouseBreakdown = orderProductCheckIns.some((p) => p.hasWarehouseSplit);

  const getRemainingTotal = (row: OrderProductCheckInInputField): number => {
    const existingQtyDifference = existingPOReceivingDiscrepancy?.orderProductDiscrepancies.find(
      (discrepancy) => discrepancy.productId === row.productId
    )?.qtyDifference;

    if (existingPOReceivingDiscrepancy && existingQtyDifference === undefined) {
      return 0;
    }
    if (existingQtyDifference !== undefined) {
      return existingQtyDifference < 0 ? 0 : existingQtyDifference;
    }
    return row.confirmedQty ?? row.orderQty;
  };

  const getRemainingForWarehouse = (row: OrderProductCheckInInputField, warehouse: "uk" | "eu"): number => {
    const existingDiscrepancy = existingPOReceivingDiscrepancy?.orderProductDiscrepancies.find(
      (discrepancy) => discrepancy.productId === row.productId
    );

    const expected =
      warehouse === "uk" ? (row.confirmedUkQty ?? row.ukQty ?? 0) : (row.confirmedEuQty ?? row.euQty ?? 0);

    if (!existingDiscrepancy) {
      return expected;
    }

    const alreadyReceived =
      warehouse === "uk" ? (existingDiscrepancy.receivedUkQty ?? 0) : (existingDiscrepancy.receivedEuQty ?? 0);
    const remaining = expected - alreadyReceived;
    return remaining < 0 ? 0 : remaining;
  };

  const productCheckInTableColumns: TableColumn<ProductCheckInColumnHeader, OrderProductCheckInInputField>[] = [
    {
      header: ProductCheckInColumnHeader.PRODUCT_ID,
      column: (row: OrderProductCheckInInputField) => (
        <span className="text-sm font-medium text-secondary-900">{row.productId}</span>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: ProductCheckInColumnHeader.PRODUCT_NAME,
      column: (row: OrderProductCheckInInputField) => (
        <span className="text-sm text-secondary-700">{row.productName}</span>
      ),
      styles: { minWidth: "160px" }
    },
    {
      header: ProductCheckInColumnHeader.PRODUCT_SKU,
      column: (row: OrderProductCheckInInputField) => (
        <span className="text-sm font-mono text-secondary-600">{row.productSkuName ?? "—"}</span>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: ProductCheckInColumnHeader.PRODUCT_BARCODE,
      column: (row: OrderProductCheckInInputField) => (
        <span className="text-sm font-mono text-secondary-600">{row.productBarcode ?? "—"}</span>
      ),
      styles: { minWidth: "120px" }
    },
    ...(showWarehouseBreakdown
      ? [
          {
            header: (
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs">Qty Received</span>
                <span className="text-[11px] font-normal tracking-wider opacity-70">(auto)</span>
              </div>
            ),
            column: (row: OrderProductCheckInInputField) => {
              const current = orderProductCheckIns.find((p) => p.fieldId === row.fieldId);
              const total = (Number(current?.receivedUkQty) || 0) + (Number(current?.receivedEuQty) || 0);
              return (
                <div className="flex items-center justify-center">
                  <span className="font-semibold text-secondary-900 tabular-nums text-sm">{total}</span>
                </div>
              );
            },
            styles: { minWidth: "100px", textAlign: "center" as const }
          },
          {
            header: <span className="text-violet-300 text-xs font-semibold tracking-wider">UK Received</span>,
            column: (row: OrderProductCheckInInputField) => {
              if (!row.hasWarehouseSplit) {
                return <span className="text-sm text-gray-400">-</span>;
              }
              const value = (
                orderProductCheckIns.find((p) => p.fieldId === row.fieldId)?.receivedUkQty ?? ""
              ).toString();
              const remainingUk = getRemainingForWarehouse(row, "uk");
              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-received-uk-quantity-${row.fieldId}`}
                    value={value}
                    onChange={(val) => handleSplitQtyChange(row.fieldId, "receivedUkQty", val)}
                    maxLength={7}
                    placeholder={`/ ${remainingUk}`}
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          },
          {
            header: <span className="text-orange-300 text-xs font-semibold tracking-wider">EU Received</span>,
            column: (row: OrderProductCheckInInputField) => {
              if (!row.hasWarehouseSplit) {
                return <span className="text-sm text-gray-400">-</span>;
              }
              const value = (
                orderProductCheckIns.find((p) => p.fieldId === row.fieldId)?.receivedEuQty ?? ""
              ).toString();
              const remainingEu = getRemainingForWarehouse(row, "eu");
              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-received-eu-quantity-${row.fieldId}`}
                    value={value}
                    onChange={(val) => handleSplitQtyChange(row.fieldId, "receivedEuQty", val)}
                    maxLength={7}
                    placeholder={`/ ${remainingEu}`}
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          }
        ]
      : [
          {
            header: ProductCheckInColumnHeader.RECEIVED_QTY,
            column: (row: OrderProductCheckInInputField) => {
              const receivedQty =
                orderProductCheckIns
                  .find((orderProductCheckIn) => orderProductCheckIn.fieldId === row.fieldId)
                  ?.receivedQty?.toString() || "";

              const receivedQtyDenominator = getRemainingTotal(row);

              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-received-quantity-${row.fieldId}`}
                    value={receivedQty}
                    onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "receivedQty", value)}
                    maxLength={40}
                    placeholder={`/ ${receivedQtyDenominator}`}
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          }
        ]),
    {
      header: ProductCheckInColumnHeader.EXPIRY_DATES,
      column: (row: OrderProductCheckInInputField) => {
        const current = orderProductCheckIns.find((orderProductCheckIn) => orderProductCheckIn.fieldId === row.fieldId);
        const { expiryDates = [] } = current ?? {};
        const totalQty = current?.hasWarehouseSplit
          ? ((Number(current?.receivedUkQty) || 0) + (Number(current?.receivedEuQty) || 0)).toString()
          : (current?.receivedQty ?? "");
        return (
          <div>
            <DynamicDateQtyInputList
              id={`${row.fieldKey}-${row.fieldId}`}
              inputDates={expiryDates}
              onInputChange={(updatedExpiryDates: DateQtyInputField[]) =>
                handleProductCheckInsInputChange(row.fieldId, "expiryDates", updatedExpiryDates)
              }
              totalQty={totalQty}
            />
          </div>
        );
      },
      styles: { minWidth: "200px" }
    }
  ];

  return <Table columns={productCheckInTableColumns} data={orderProductCheckIns} />;
};

export { OrderProductCheckInInputTable };
